import {Grant, FamilyPlan} from 'shared/redux/product/ProductTypes'
import {ProfileSlice} from 'shared/redux/profile/ProfileTypes'
import Cookies from 'universal-cookie'
import { AD_ATTRIBUTES } from './constants'
import AnalyticsManager from '../services/AnalyticsManager'

const cookie = new Cookies()

export enum Environments {
  local = 'local',
  sandbox = 'sandbox',
  development = 'development',
  staging = 'staging',
  production = 'production',
}
export type LogLevels = 'DEBUG' | 'INFO' | 'ERROR'
export const currentEnvironment = (): Environments => {
  if (process.env.REACT_APP_LOCAL) {
    return Environments.local
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('sandbox')) {
    return Environments.sandbox
  } else if (
    (process.env.REACT_APP_API_FQDN as string).includes('development')
  ) {
    return Environments.development
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('staging')) {
    return Environments.staging
  }

  return Environments.production
}

export const logLevel = (loggerLevel: LogLevels): LogLevels => {
  if (currentEnvironment() === Environments.local) {
    return 'DEBUG'
  } else {
    return loggerLevel
  }
}

export const bytesToGbConverter = (size: number): number => {
  return size / (1000 * 1000 * 1000)
}

export const compositeCMSString = (
  cmsString: string,
  replacement: string | string[],
  replacing = '{0}',
): string => {
  try {
    if (Array.isArray(replacement)) {
      let replacedString = cmsString
      replacement.forEach((replace, index) => {
        replacedString = replacedString.replace(`{${index}}`, replace)
      })
      return replacedString
    } else {
      return cmsString.replace(replacing, replacement)
    }
  } catch (error) {
    // fail silently. don't worry it's safe
    return ''
  }
}

export const isObjectEmpty = (obj: any): boolean =>
  obj && Object.keys(obj).length === 0

export const isValidDate = (d: any): boolean => {
  return d instanceof Date && !isNaN(d.getTime())
}

export const formatDate = (date: string): string => {
  const newDate = new Date(date)

  return isValidDate(newDate)
    ? `${newDate.getMonth() + 1}/${newDate.getDate()}/${newDate.getFullYear()}`
    : ''
}

// zips 2 arrays in order - if the lengths are different, returns an array of the larger length with missing elements
// returned as undefined
export const zip = <T, U>(a1: T[], a2: U[]): [T?, U?][] => {
  if (a2.length > a1.length) {
    return a2.map((a, i) => [a1[i], a])
  } else {
    return a1.map((a, i) => [a, a2[i]])
  }
}

// from https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html
export const assertNever = (x: never): never => {
  throw new Error('Unexpected object: ' + x)
}

export const nodeToString = (node: any): string => {
  if (typeof node === 'string') return node;

  if (node?.props?.children) {
    if (typeof node.props.children === 'string') return node.props.children;
    if (Array.isArray(node.props.children)) return node.props.children.map(nodeToString).join(' ');
    return String(node.props.children ?? '');
  }
  return '';
}

export const getCol4DisplayValue = (status: string): string => {
  if (status === 'not_found') return 'Not Found';
  if (['optout_in_progress', 'new'].includes(status)) return 'Removing';
  return 'Removed';
}

export const decodeString=(encodedStr: string)=>{
    const percentDecoded = decodeURIComponent(encodedStr);
    const jsonStr = atob(percentDecoded);
    const data = JSON.parse(jsonStr);
    return data;
}
export const validateEmail = (email: string) => {
  if (!email) {
    return false
  }
  // this beautiful construction is a classic from
  // https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
  return !!email.toLowerCase().match(
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
  )
}

export const isAddressIncomplete = (address: any, grant?: Grant) => {
  const requiredFields = ['city', 'state']
  if (grant) requiredFields.push('streetAddress', 'zip')
  return requiredFields.some(field => !address[field]?.trim())
}

export const formatPhoneNumbers = (
  phones: { value: string }[] = []
): { value: string }[] => {
  return phones.map((p) => ({
    value: `+1${p.value.replace(/[^\d]/g, '')}`,
  }));
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;
  return emailRegex.test(email.trim());
};

export const formatPhoneNumberDisplay = (value?: string) => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');

  if (digits.length <= 3) return digits;
  if (digits.length <= 6)
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const setFamilyPromptCookie=()=>{
  const familyPlanCookie = cookie.get(
    'PromptForFamilyPlan'
  )
  if(!familyPlanCookie){
    cookie.set('PromptForFamilyPlan', 'true', {
      path: '/',
      maxAge: 60 * 60 * 24 * 365 * 100,
    })
  }
}

export function normalizeCity(value: string): string {
  if (!value) return "";

  let normalized = value.replace(/_/g, " ").trim();
  normalized = normalized
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return normalized;
}
 
export const normalizeMiddleName = (value: string): string => {
  if (!value) return "";

  if (value.toLowerCase() === "nmn") {
    return "NMN";
  }
  return value;
};

export function shouldShowProfileCompletionAlert(profile: ProfileSlice): boolean {
  if (!profile.last_scan_initiated_at) {
    return false
  }
  const ALERT_INTERVAL_MS = 24 * 60 * 60 * 1000
  const hasFewerThanRequiredAddresses = profile.address.length < 3

  const currentTimestamp = Date.now()
  const lastScanTimestamp = new Date(profile.last_scan_initiated_at).getTime()

  const isScanOlderThan24Hours =
    lastScanTimestamp + ALERT_INTERVAL_MS < currentTimestamp

  return hasFewerThanRequiredAddresses && isScanOlderThan24Hours

}

export const setAdAttributes = () => {
  const params = new URLSearchParams(window.location.search);
  const adAttributes : Record<string, string> = {};

  AD_ATTRIBUTES.forEach((key) => {
    const value = params.get(key);

    if (value) {
      adAttributes[key] = value;

      cookie.set(key, value, {
        path: '/',
        maxAge: 60 * 60 * 24 * 365 * 100,
      });
    }
  });

  if (Object.keys(adAttributes).length > 0) {
    AnalyticsManager.registerSuperProperties(adAttributes);
  }

};

export const getAdAttributes = () => {
  const attributes : Record<string, string> = {}

  AD_ATTRIBUTES.forEach((key) => {
    const value = cookie.get(key);

    if (value) {
      if(key==="_fbc"){
        attributes.fbc = value;
      }else{
        attributes[key] = value;
      }
    }
  });

  return attributes
}

export const getMetapixelAttributes = () =>{
  const sessionCookie = cookie.get("_fbp")
  const clickID = cookie.get("_fbc")
  return {
    "fbp": sessionCookie,
    "fbc": clickID,
  }
}

export const getMixpanelUserAttributes = async() => {
  const distinctId = AnalyticsManager.getDistinctId();
  const deviceId = await AnalyticsManager.getSuperProperty('$device_id');

  return {
    distinct_id: distinctId,
    device_id: deviceId,
  };
};

export const getFirebaseUserAttributes = () => {
  const attributes : Record<string, string | null> = {
    client_id: null,
    session_id: null,
    session_number: null,
    session_id_marketing: null,
    session_number_marketing: null,
  };

  const marketingMeasurementId = process.env.REACT_APP_MARKETING_MEASUREMENT_ID;
  const meprismMeasurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

  // Helper: parse _ga cookie -> client_id
  const parseClientId = (gaCookieValue: string | null) => {
    if (!gaCookieValue) return null;
    const parts = gaCookieValue.split('.');
    // format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
    return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
  };

  // Helper: parse _ga_XXXX session cookie -> { sessionId, sessionNumber }
  const parseSessionCookie = (sessionCookieValue: string | null) => {
    if (!sessionCookieValue) return { sessionId: null, sessionNumber: null };

    // GS2 format: GS2.1.s1747132561$o1$g0$t1747132655$j0$l0$h0
    if (sessionCookieValue.startsWith('GS2')) {
      const dollarParts = sessionCookieValue.split('$');
      
      let sessionId: string | null = null;
      let sessionNumber: string | null = null;

      for (const part of dollarParts) {
        if (part.includes('s') && part.split('.').length >= 3) {
          // First segment: "GS2.1.s<sessionId>" — extract after last dot + 's'
          const match = part.match(/s(\d+)$/);
          if (match) sessionId = match[1];
        } else if (part.startsWith('o')) {
          sessionNumber = part.slice(1) || null;
        }
      }

      return { sessionId, sessionNumber };
    }

    // Legacy GS1 format: GS1.1.<sessionId>.<sessionNumber>.<sessionEngaged>...
    const parts = sessionCookieValue.split('.');
    return {
      sessionId: parts[2] ?? null,
      sessionNumber: parts[3] ?? null,
    };
  };

  // Helper: build session cookie key from measurement ID
  const getSessionCookieKey = (measurementId: string | null) => {
    if (!measurementId) return null;
    return `_ga_${measurementId.replace('G-', '')}`;
  };

  const gaCookie = cookie.get('_ga');
  attributes.client_id = parseClientId(gaCookie);

  // --- Meprism (Firebase) attributes ---
  if (meprismMeasurementId) {
    const meprismSessionKey = getSessionCookieKey(meprismMeasurementId);
    const meprismSessionCookie = meprismSessionKey ? cookie.get(meprismSessionKey) : null;

    const { sessionId, sessionNumber } = parseSessionCookie(meprismSessionCookie);
    attributes.session_id = sessionId;
    attributes.session_number = sessionNumber;
  }

  // --- Marketing attributes (only if marketing session cookie exists) ---
  if (marketingMeasurementId) {
    const marketingSessionKey = getSessionCookieKey(marketingMeasurementId);
    const marketingSessionCookie = marketingSessionKey ? cookie.get(marketingSessionKey) : null;

    // Only populate marketing attributes if the session cookie is present
    if (marketingSessionCookie) {
      const { sessionId, sessionNumber } = parseSessionCookie(marketingSessionCookie);
      attributes.session_id_marketing = sessionId;
      attributes.session_number_marketing = sessionNumber;
    }
  }

  return attributes;
}

export function shouldShowGettingStartedModal(
  profile: ProfileSlice | null,
  grant: Grant | null | undefined,
  brokerListLength: number,
  brokersRequireRemoval: string[] = [],
  familyPlan: FamilyPlan | null = null
): boolean {
  if (!profile || !!profile.metadata?.getting_started_completed) return false
  if (!profile.scan_completed_mail_sent_at) return false

  const hasMissingMiddleName = !profile.names?.middle?.[0]
  const hasEmptyStreet = !profile.address?.length || profile.address?.some(addr => !addr?.street1?.trim())
  const hasEmptyPhoneNumber = profile.phones?.length === 0
  const hasMissingAuthLetter = !profile.auth_letter_s3_key
  const isBusinessUser = grant?.grant_source === 'corporate_grant'
  const isFamilyUser = grant?.sku?.split('.')[2] === 'fam'
  
  const hasMissingAdditionalAddresses = isBusinessUser
    ? (profile.address?.length || 0) <= 1
    : shouldShowProfileCompletionAlert(profile) && brokerListLength < 50

  const purchaseDate = grant?.grant_purchase_date
  const formattedDate = purchaseDate && !purchaseDate.includes('Z') && !purchaseDate.includes('+') 
    ? (purchaseDate.includes(' ') ? purchaseDate.replace(' ', 'T') : purchaseDate) + 'Z' 
    : purchaseDate
  const subscriptionAgeInMs = formattedDate ? new Date().getTime() - new Date(formattedDate).getTime() : 0
  const isSubscriptionOldEnough = (!!grant && subscriptionAgeInMs > 60 * 60 * 1000) || isBusinessUser ||  grant?.grant_source === 'family_grant'

  const isPeopleFindersCompleted = profile.metadata?.todo_actions?.some(
    (a) => a.action_category === 'broker' && a.broker_name === 'peoplefinders' && (a.action === 'confirmator' || a.action === 'later' || a.action === 'removal'),
  )
  const isInteliusCompleted = profile.metadata?.todo_actions?.some(
    (a) => a.action_category === 'broker' && a.broker_name === 'intelius' && (a.action === 'confirmator' || a.action === 'later' || a.action === 'removal'),
  )
  const isDataCommAcknowledged = !!profile.metadata?.todo_actions?.some((a) => (a.action_category === 'data_comm_acknowledgement' && a.action === 'acknowledge') || (a.action_category === 'data_comm_acknowledgement' && a.action === 'later'))

  const isPeopleFindersNotNeeded = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('peoplefinders')) && !isPeopleFindersCompleted;
  const isInteliusNotNeeded = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('intelius')) && !isInteliusCompleted;

  const isRemovalCompleted = (isPeopleFindersCompleted || isPeopleFindersNotNeeded) && 
    (isBusinessUser || isInteliusCompleted || isInteliusNotNeeded) && 
    isDataCommAcknowledged

  const isPlusOneIncomplete = isFamilyUser && !(familyPlan?.email || ['invited', 'accepted', 'queued'].includes(familyPlan?.invite_status || ''))

  const isProfileIncomplete = (
    hasMissingMiddleName || 
    hasEmptyStreet || 
    hasEmptyPhoneNumber || 
    hasMissingAdditionalAddresses ||
    isPlusOneIncomplete
  ) || hasMissingAuthLetter
  return isSubscriptionOldEnough && (isProfileIncomplete || !isRemovalCompleted)
}


export const setUserIp = async() => {
  const res = await fetch("https://api.ipify.org?format=json");
  const data = await res.json();
  cookie.set("ip",data.ip, {
    path: '/',
    maxAge: 60 * 60 * 24 * 365 * 100,
  })
}

export const shouldShowTodoList = (grant: Grant | undefined, profile: ProfileSlice) => {
  const isBusinessUser = grant?.grant_source === 'corporate_grant'
  const isFamilyUser = grant?.grant_source === 'family_grant'

  const isScanCompleted = !!profile.scan_completed_mail_sent_at
  const purchaseDate = grant?.grant_purchase_date
  const formattedDate = purchaseDate && !purchaseDate.includes('Z') && !purchaseDate.includes('+') 
    ? (purchaseDate.includes(' ') ? purchaseDate.replace(' ', 'T') : purchaseDate) + 'Z' 
    : purchaseDate
  const subscriptionAgeInMs = formattedDate ? new Date().getTime() - new Date(formattedDate).getTime() : 0
  const isSubscriptionOldEnough = (!!grant && subscriptionAgeInMs > 60 * 60 * 1000) || isBusinessUser || isFamilyUser

  return isScanCompleted && isSubscriptionOldEnough

}